import { Badge, Button, Card, EmptyState, Input, PageHeader, Skeleton } from '@provenance/design';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type { SupportChamber } from '../lib/support';

/**
 * Where a Provenir support officer chooses the chamber to work in (D-031).
 *
 * Standing access to every chamber, one at a time. Everything done after choosing appears in
 * that chamber's history under the officer's name, marked as Provenir Support.
 */
export function SupportChambers() {
  const { user, chooseChamber } = useSession();
  const [q, setQ] = useState('');
  const chambers = useQuery({
    queryKey: ['support-chambers'],
    queryFn: () => api.get<SupportChamber[]>('/api/console/support/chambers'),
  });

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (chambers.data ?? []).filter(
      (c) => !needle || `${c.shortName} ${c.legalName} ${c.slug}`.toLowerCase().includes(needle),
    );
  }, [chambers.data, q]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Choose a chamber"
        lede="Pick the chamber you are helping. Everything you do there appears in its history under your name, marked as Provenir Support. You can switch at any time."
        actions={
          <>
            <Button variant="secondary" asChild leadingIcon={<Search className="size-3.5" />}>
              <Link to="/support-desk">Search every chamber</Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/support/integrators">Integrators</Link>
            </Button>
          </>
        }
      />

      <div className="relative max-w-md">
        <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--pv-faint)]" />
        <Input
          aria-label="Find a chamber"
          placeholder="Find a chamber"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>

      {chambers.isLoading ? (
        <Skeleton className="h-48" />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-5" />}
          title={q ? 'No chamber matches that' : 'No active chambers yet'}
          description={q ? 'Try the chamber’s short name or part of its legal name.' : 'A Portal Admin creates chambers.'}
        />
      ) : (
        <ul className="grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((c) => {
            const current = user?.chamberId === c.id;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => chooseChamber(c)}
                  className="w-full text-left"
                  aria-current={current || undefined}
                >
                  <Card className="flex items-center gap-3 transition-colors hover:border-[var(--pv-line-strong)]">
                    {c.logoUrl ? (
                      <img src={c.logoUrl} alt="" className="h-9 w-16 shrink-0 object-contain object-left" />
                    ) : (
                      <span className="grid size-9 shrink-0 place-items-center rounded-[var(--pv-radius)] bg-[var(--pv-card-alt)] [font-size:12px] font-bold">
                        {c.shortName.slice(0, 3).toUpperCase()}
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{c.shortName}</span>
                      <span className="block truncate [font-size:12.5px] text-[var(--pv-muted)]">{c.legalName}</span>
                    </span>
                    {c.sandbox && <Badge tone="warn" size="sm">Practice</Badge>}
                    {current && (
                      <Badge tone="ok" size="sm">
                        <Check aria-hidden className="size-3" /> Working here
                      </Badge>
                    )}
                  </Card>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
